import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown,
  PoundSterling,
  Download,
  Settings,
  Calculator,
  AlertTriangle,
  Info
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Legend, ComposedChart, Line } from 'recharts';
import {
  MONTH_NAMES,
  FORECAST_MONTHS,
  DEFAULT_ASSUMPTIONS,
  SCENARIO_WEDDINGS,
  formatCurrency,
  formatCurrencyFull,
} from './forecasting/constants';

export default function ForecastingTab({ confirmedBalances = {}, calendarInfo = null, latestAvgSale = null }) {
  const avgValue = latestAvgSale || DEFAULT_ASSUMPTIONS.avgWeddingValue;
  const [assumptions, setAssumptions] = useState({ ...DEFAULT_ASSUMPTIONS, avgWeddingValue: avgValue });

  // Update avgWeddingValue when latestAvgSale arrives from sales data
  useEffect(() => {
    if (latestAvgSale) {
      setAssumptions(a => ({ ...a, avgWeddingValue: latestAvgSale }));
    }
  }, [latestAvgSale]);

  // Dynamic scenarios based on current avg wedding value
  const scenarios = useMemo(() => ({
    A: { weddings: 3, monthlyValue: 3 * assumptions.avgWeddingValue, label: '3 weddings/month' },
    B: { weddings: 5, monthlyValue: 5 * assumptions.avgWeddingValue, label: '5 weddings/month' },
    C: { weddings: 10, monthlyValue: 10 * assumptions.avgWeddingValue, label: '10 weddings/month' },
    D: { weddings: 15, monthlyValue: 15 * assumptions.avgWeddingValue, label: '15 weddings/month' },
    E: { weddings: 20, monthlyValue: 20 * assumptions.avgWeddingValue, label: '20 weddings/month' },
    F: { weddings: 25, monthlyValue: 25 * assumptions.avgWeddingValue, label: '25 weddings/month' }
  }), [assumptions.avgWeddingValue]);

  const [scenarioPerMonth, setScenarioPerMonth] = useState(
    FORECAST_MONTHS.reduce((acc, m) => ({ ...acc, [m.key]: 'C' }), {})
  );
  const [activeScenario, setActiveScenario] = useState('C');
  const [additionalRevenue, setAdditionalRevenue] = useState(
    FORECAST_MONTHS.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {})
  );
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [costsUnlocked, setCostsUnlocked] = useState(false);
  const [showModelledBalance, setShowModelledBalance] = useState(true);
  const [teamScaling, setTeamScaling] = useState(false);
  const [summaryRange, setSummaryRange] = useState(36);

  const BASELINE_WEDDINGS = 10;
  const SCALABLE_KEYS = ['alex', 'ellis', 'shannon', 'amie', 'andy', 'subcontractors'];

  // Cost line items definition for rendering
  const COST_LINE_ITEMS = [
    { section: 'Payroll', items: [
      { key: 'alexSalary', costKey: 'alex', label: 'Alex Salary' },
      { key: 'ellisSalary', costKey: 'ellis', label: 'Ellis Salary' },
      { key: 'shannonSalary', costKey: 'shannon', label: 'Shannon Salary' },
      { key: 'amieSalary', costKey: 'amie', label: 'Amie Salary' },
      { key: 'andySalary', costKey: 'andy', label: 'Andy Salary' },
    ]},
    { section: 'Fixed Overheads', items: [
      { key: 'rent', costKey: 'rent', label: 'Rent' },
      { key: 'businessRates', costKey: 'businessRates', label: 'Business Rates' },
      { key: 'electric', costKey: 'electric', label: 'Electric' },
      { key: 'internet', costKey: 'internet', label: 'Internet' },
      { key: 'phoneSystem', costKey: 'phoneSystem', label: 'Phone System' },
      { key: 'eeMobiles', costKey: 'eeMobiles', label: 'EE Mobiles' },
      { key: 'bupaHealth', costKey: 'bupaHealth', label: 'Bupa Health' },
      { key: 'insurance', costKey: 'insurance', label: 'Business Insurance' },
    ]},
    { section: 'Subscriptions', items: [
      { key: 'accountant', costKey: 'accountant', label: 'Accountant' },
      { key: 'hitched', costKey: 'hitched', label: 'Hitched' },
      { key: 'emailsCRM', costKey: 'emailsCRM', label: 'Emails / CRM' },
    ]},
    { section: 'Marketing', items: [
      { key: 'weddingFairs', costKey: 'weddingFairs', label: 'Wedding Fairs' },
      { key: 'fuelCards', costKey: 'fuelCards', label: 'Fuel Cards' },
      { key: 'agencyAds', costKey: 'agencyAds', label: 'Agency / FB / Google Ads' },
    ]},
    { section: 'Variable', items: [
      { key: 'subcontractors', costKey: 'subcontractors', label: 'Subcontractors' },
      { key: 'repairsMaintenance', costKey: 'repairsMaintenance', label: 'Repairs & Maintenance' },
      { key: 'miscTravel', costKey: 'miscTravel', label: 'Misc Travel' },
      { key: 'otherVariable', costKey: 'otherVariable', label: 'Other Variable' },
    ]},
  ];

  // Set all months to a given scenario
  const applyScenarioToAll = (scenario) => {
    setActiveScenario(scenario);
    setScenarioPerMonth(FORECAST_MONTHS.reduce((acc, m) => ({ ...acc, [m.key]: scenario }), {}));
  };

  // Wedding distribution weights from ICS calendar (for modelling new sales balance timing)
  const weddingWeights = useMemo(() => {
    if (calendarInfo?.weddingDistribution) {
      const dist = {};
      for (let i = 0; i < 12; i++) {
        dist[i] = calendarInfo.weddingDistribution[String(i)] || 0;
      }
      return dist;
    }
    // Fallback if calendar not loaded yet
    return { 0: 0, 1: 0.006, 2: 0.072, 3: 0.096, 4: 0.15, 5: 0.156, 6: 0.12, 7: 0.198, 8: 0.072, 9: 0.066, 10: 0.036, 11: 0.03 };
  }, [calendarInfo?.weddingDistribution]);

  // New Sales Deposits = 50% of scenario monthly value (received at time of sale)
  const calculateScenarioDeposits = useMemo(() => {
    const result = {};
    FORECAST_MONTHS.forEach((month) => {
      result[month.key] = {};
      ['A', 'B', 'C', 'D', 'E', 'F'].forEach(scenario => {
        result[month.key][scenario] = scenarios[scenario].monthlyValue * 0.5;
      });
    });
    return result;
  }, [scenarios]);

  // Modelled Balance Income = 50% of new sales, distributed using 4-24 month window
  // Methodology (from Excel): For each sale month, the 50% balance is distributed
  // across months 4-24 after the sale, using wedding date weights normalized within
  // the available window. Balance arrives 1 month before the predicted wedding.
  const calculateModelledBalances = useMemo(() => {
    const result = {};
    // Initialise all months to 0 for each scenario
    FORECAST_MONTHS.forEach(m => {
      result[m.key] = {};
      ['A', 'B', 'C', 'D', 'E', 'F'].forEach(s => { result[m.key][s] = 0; });
    });

    // Build a lookup of all forecast month keys for boundary checking
    const forecastKeys = new Set(FORECAST_MONTHS.map(m => m.key));

    FORECAST_MONTHS.forEach((saleMonth) => {
      // Calculate the 4-24 month window from this sale month
      const saleDate = new Date(saleMonth.year, saleMonth.month, 1);

      // Collect all valid wedding months in the 4-24 month window and their weights
      const windowMonths = [];
      for (let offset = 5; offset <= 25; offset++) {
        // offset 5-25 months for wedding (so balance at offset 4-24, i.e. 1 month before)
        const weddingDate = new Date(saleDate);
        weddingDate.setMonth(weddingDate.getMonth() + offset);
        const weddingCalMonth = weddingDate.getMonth(); // 0-indexed
        const weight = weddingWeights[weddingCalMonth] || 0;
        if (weight === 0) continue;

        // Balance due = 1 month before wedding
        const balDate = new Date(weddingDate);
        balDate.setMonth(balDate.getMonth() - 1);
        const balKey = `${balDate.getFullYear()}-${String(balDate.getMonth() + 1).padStart(2, '0')}`;

        // Only include if within forecast range
        if (forecastKeys.has(balKey)) {
          windowMonths.push({ balKey, weight });
        }
      }

      // Normalize weights within this window so they sum to 1
      const totalWeight = windowMonths.reduce((sum, m) => sum + m.weight, 0);
      if (totalWeight === 0) return;

      ['A', 'B', 'C', 'D', 'E', 'F'].forEach(scenario => {
        const totalBalance = scenarios[scenario].monthlyValue * 0.5;

        windowMonths.forEach(({ balKey, weight }) => {
          const normalizedWeight = weight / totalWeight;
          result[balKey][scenario] += totalBalance * normalizedWeight;
        });
      });
    });

    return result;
  }, [scenarios, weddingWeights]);

  // Calculate monthly costs
  const calculateMonthlyCosts = useMemo(() => {
    const result = {};
    
    FORECAST_MONTHS.forEach(month => {
      // Subcontractors: flat monthly cost
      const subcontractors = assumptions.subcontractors;
      
      const costs = {
        // Fixed Overheads
        rent: assumptions.rent,
        businessRates: assumptions.businessRates,
        electric: assumptions.electric,
        internet: assumptions.internet,
        phoneSystem: assumptions.phoneSystem,
        eeMobiles: assumptions.eeMobiles,
        bupaHealth: assumptions.bupaHealth,
        insurance: assumptions.insurance,
        // Subscriptions
        accountant: assumptions.accountant,
        hitched: assumptions.hitched,
        emailsCRM: assumptions.emailsCRM,
        // Payroll
        alex: assumptions.alexSalary,
        ellis: assumptions.ellisSalary,
        shannon: assumptions.shannonSalary,
        amie: assumptions.amieSalary,
        andy: assumptions.andySalary,
        // Marketing
        weddingFairs: assumptions.weddingFairs,
        fuelCards: assumptions.fuelCards,
        agencyAds: assumptions.agencyAds,
        // Variable
        subcontractors,
        repairsMaintenance: assumptions.repairsMaintenance,
        miscTravel: assumptions.miscTravel,
        otherVariable: assumptions.otherVariable
      };
      
      costs.total = Object.values(costs).reduce((sum, val) => sum + val, 0);
      result[month.key] = costs;
    });
    
    return result;
  }, [assumptions]);

  // Calculate full cashflow
  const cashflowData = useMemo(() => {
    const result = [];
    let openingBalance = assumptions.openingBalance;
    
    FORECAST_MONTHS.forEach((month, index) => {
      const scenario = scenarioPerMonth[month.key] || 'C';
      const confirmedBalance = confirmedBalances[month.key] || 0;
      const deposit = calculateScenarioDeposits[month.key]?.[scenario] || 0;
      const modelledBalance = showModelledBalance ? (calculateModelledBalances[month.key]?.[scenario] || 0) : 0;
      const additional = additionalRevenue[month.key] || 0;
      
      // Get base costs and apply team scaling if enabled
      const baseCosts = calculateMonthlyCosts[month.key];
      let costs = baseCosts;
      let scaleFactor = 1;
      if (teamScaling && baseCosts) {
        const weddings = scenarios[scenario]?.weddings || BASELINE_WEDDINGS;
        scaleFactor = weddings / BASELINE_WEDDINGS;
        costs = { ...baseCosts };
        SCALABLE_KEYS.forEach(key => {
          costs[key] = (baseCosts[key] || 0) * scaleFactor;
        });
        costs.total = Object.entries(costs)
          .filter(([k]) => k !== 'total')
          .reduce((sum, [, v]) => sum + v, 0);
      }
      
      const totalCashIn = confirmedBalance + deposit + modelledBalance + additional;
      const vat = totalCashIn / 6; // VAT at 20% on VAT-inclusive income
      const totalCashOut = (costs?.total || 0) + vat;
      const netCashflow = totalCashIn - totalCashOut;
      const closingBalance = openingBalance + netCashflow;
      
      result.push({
        ...month,
        scenario,
        scaleFactor,
        confirmedBalance,
        deposit,
        modelledBalance,
        additional,
        totalCashIn,
        vat,
        costs,
        totalCashOut,
        netCashflow,
        openingBalance,
        closingBalance
      });
      
      openingBalance = closingBalance;
    });
    
    return result;
  }, [scenarioPerMonth, additionalRevenue, confirmedBalances, calculateScenarioDeposits, calculateModelledBalances, calculateMonthlyCosts, assumptions.openingBalance, showModelledBalance, teamScaling, scenarios]);

  // Download CSV
  const negativeMonths = useMemo(() => cashflowData.filter(m => m.closingBalance < 0), [cashflowData]);

  const downloadCSV = () => {
    const headers = [
      'Month',
      'Scenario',
      'Confirmed Balance',
      'New Sales Deposits (50%)',
      'Modelled Balance Income (50%)',
      'Additional Revenue',
      'Total Cash In',
      'VAT (20%)',
      'Total Cash Out',
      'Net Cashflow',
      'Opening Balance',
      'Closing Balance'
    ];
    
    const rows = cashflowData.map(row => [
      row.fullLabel,
      row.scenario,
      row.confirmedBalance,
      row.deposit.toFixed(2),
      row.modelledBalance.toFixed(2),
      row.additional,
      row.totalCashIn.toFixed(2),
      row.vat.toFixed(2),
      row.totalCashOut.toFixed(2),
      row.netCashflow.toFixed(2),
      row.openingBalance.toFixed(2),
      row.closingBalance.toFixed(2)
    ]);
    
    const csvContent = [
      'WVE CASHFLOW FORECAST',
      `Generated: ${new Date().toLocaleString()}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `WVE_Cashflow_Forecast_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <Card className="bg-gold-50 border-gold-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-gold-600 mt-0.5" />
            <div className="text-sm text-gold-800">
              <p className="font-medium mb-1">Client Cashflow Forecast</p>
              <ul className="text-xs space-y-0.5 text-gold-700">
                <li>• Blue cells = inputs you can edit</li>
                <li>• Confirmed balance payments sourced live from your ICS calendar ({calendarInfo?.weddingCount || 0} weddings) — 40% of total fee, due 1 month before wedding (conservative — accounts for fully paid clients)</li>
                <li>• New sales: 50% deposit on booking + 50% balance modelled by wedding season</li>
                <li>• All income is VAT-inclusive — 20% VAT is deducted as the first cost line</li>
                <li>• Scenarios: A (3/mo), B (5/mo), C (10/mo), D (15/mo), E (20/mo), F (25/mo)</li>
                <li>• Team Scaling: When enabled, payroll & subcontractor costs scale pro-rata above 10 weddings/mo baseline</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Modelling Box */}
      <Card className="bg-white border-stone-100 shadow-sm" data-testid="scenario-modelling-box">
        <CardHeader className="pb-3 border-b border-stone-100">
          <CardTitle className="text-base font-medium text-stone-800 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gold-600" />
            New Wedding Sales — Scenario Modelling
          </CardTitle>
          <p className="text-xs text-stone-500 mt-1">Based on avg. wedding value of {formatCurrency(assumptions.avgWeddingValue)} · 50% deposit on booking, 50% balance before wedding</p>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-5">
            {Object.entries(scenarios).map(([key, s]) => (
              <div
                key={key}
                className={`rounded-xl border p-4 transition-all cursor-pointer hover:shadow-md ${
                  activeScenario === key ? 'border-gold-300 bg-gold-50 ring-2 ring-gold-400' : 'border-stone-200 bg-stone-50 hover:border-stone-300'
                }`}
                onClick={() => applyScenarioToAll(key)}
                data-testid={`scenario-card-${key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-lg font-bold ${activeScenario === key ? 'text-gold-700' : 'text-stone-800'}`}>
                    Scenario {key}
                  </span>
                  {activeScenario === key && (
                    <span className="text-[10px] bg-gold-500 text-white px-1.5 py-0.5 rounded-full font-medium">Active</span>
                  )}
                </div>
                <p className="text-sm font-semibold text-stone-700">{s.weddings} weddings / month</p>
                <div className="mt-3 space-y-1.5 text-xs text-stone-600">
                  <div className="flex justify-between">
                    <span>Monthly Sales</span>
                    <span className="font-medium text-stone-800">{formatCurrency(s.monthlyValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Deposits (50%)</span>
                    <span className="font-medium text-green-700">{formatCurrency(s.monthlyValue * 0.5)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Balances (50%)</span>
                    <span className="font-medium text-purple-700">{formatCurrency(s.monthlyValue * 0.5)}</span>
                  </div>
                  <div className="flex justify-between pt-1.5 border-t border-stone-200/60">
                    <span>Annual Revenue</span>
                    <span className="font-semibold text-stone-900">{formatCurrency(s.monthlyValue * 12)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Wedding Forecast Simulator */}
      <Card className="bg-white border-stone-100 shadow-sm" data-testid="wedding-distribution-chart">
        <CardHeader className="pb-3 border-b border-stone-100">
          <CardTitle className="text-base font-medium text-stone-800 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-purple-600" />
            Wedding Forecast Simulator
          </CardTitle>
          <p className="text-xs text-stone-500 mt-1">When new sales balance payments are expected — based on {calendarInfo?.weddingCount || 0} real wedding dates from your calendar</p>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
            {MONTH_NAMES.map((name, i) => {
              const weight = weddingWeights[i] || 0;
              const barHeight = Math.max(weight * 280, 3);
              const isPeak = weight >= 0.1;
              return (
                <div key={name} className="flex flex-col items-center gap-1.5" data-testid={`wedding-weight-${name.toLowerCase()}`}>
                  <div className="w-full flex flex-col items-center justify-end h-16">
                    <div
                      className={`w-full rounded-t transition-all ${isPeak ? 'bg-purple-500' : 'bg-stone-300'}`}
                      style={{ height: `${barHeight}px` }}
                    />
                  </div>
                  <span className="text-[11px] text-stone-600 font-medium">{name}</span>
                  <span className={`text-[10px] ${isPeak ? 'text-purple-700 font-semibold' : 'text-stone-400'}`}>
                    {(weight * 100).toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-stone-400 mt-3 text-center">
            This distribution determines when the 50% balance from modelled new sales is expected to arrive (1 month before wedding)
          </p>
        </CardContent>
      </Card>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={() => setShowAssumptions(!showAssumptions)}
          className="border-stone-200"
        >
          <Settings className="w-4 h-4 mr-2" />
          {showAssumptions ? 'Hide' : 'Show'} Assumptions
        </Button>
        <Button
          variant="outline"
          onClick={downloadCSV}
          className="border-stone-200"
        >
          <Download className="w-4 h-4 mr-2" />
          Export Forecast
        </Button>
        <button
          onClick={() => setTeamScaling(!teamScaling)}
          className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md transition-colors border"
          style={{
            background: teamScaling ? '#dbeafe' : '#f5f5f4',
            borderColor: teamScaling ? '#3b82f6' : '#d6d3d1',
            color: teamScaling ? '#1e40af' : '#78716c'
          }}
          data-testid="team-scaling-toggle"
        >
          {teamScaling ? (
            <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></svg> Team Scaling ON</>
          ) : (
            <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg> Team Scaling OFF</>
          )}
        </button>
      </div>

      {/* Assumptions Panel */}
      {showAssumptions && (
        <Card className="bg-white border-stone-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium text-stone-800 flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Model Assumptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <label className="text-xs text-stone-500 block mb-1">Alex Salary</label>
                <Input
                  type="number"
                  value={assumptions.alexSalary}
                  onChange={e => setAssumptions(a => ({ ...a, alexSalary: Number(e.target.value) }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Ellis Salary</label>
                <Input
                  type="number"
                  value={assumptions.ellisSalary}
                  onChange={e => setAssumptions(a => ({ ...a, ellisSalary: Number(e.target.value) }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Shannon Salary</label>
                <Input
                  type="number"
                  value={assumptions.shannonSalary}
                  onChange={e => setAssumptions(a => ({ ...a, shannonSalary: Number(e.target.value) }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Amie Salary</label>
                <Input
                  type="number"
                  value={assumptions.amieSalary}
                  onChange={e => setAssumptions(a => ({ ...a, amieSalary: Number(e.target.value) }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Andy Salary</label>
                <Input
                  type="number"
                  value={assumptions.andySalary}
                  onChange={e => setAssumptions(a => ({ ...a, andySalary: Number(e.target.value) }))}
                  className="h-8 text-sm"
                  data-testid="andy-salary-input"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Rent</label>
                <Input
                  type="number"
                  value={assumptions.rent}
                  onChange={e => setAssumptions(a => ({ ...a, rent: Number(e.target.value) }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Opening Balance</label>
                <Input
                  type="number"
                  value={assumptions.openingBalance}
                  onChange={e => setAssumptions(a => ({ ...a, openingBalance: Number(e.target.value) }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {(() => {
        const RANGES = [
          { key: 3, label: '3 Months' },
          { key: 6, label: '6 Months' },
          { key: 12, label: '12 Months' },
          { key: 24, label: '24 Months' },
          { key: 36, label: 'All (36 Mo)' },
        ];
        const slicedData = cashflowData.slice(0, summaryRange);
        const totalIn = slicedData.reduce((sum, m) => sum + m.totalCashIn, 0);
        const totalOut = slicedData.reduce((sum, m) => sum + m.totalCashOut, 0);
        const netCashflow = slicedData.reduce((sum, m) => sum + m.netCashflow, 0);
        const finalBalance = slicedData[slicedData.length - 1]?.closingBalance || 0;

        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-stone-600">Summary</p>
              <div className="flex bg-stone-100 rounded-lg p-0.5">
                {RANGES.map(r => (
                  <button
                    key={r.key}
                    onClick={() => setSummaryRange(r.key)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      summaryRange === r.key
                        ? 'bg-white text-stone-900 shadow-sm'
                        : 'text-stone-500 hover:text-stone-700'
                    }`}
                    data-testid={`summary-range-${r.key}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white border-stone-100">
                <CardContent className="p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Total Cash In</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(totalIn)}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-stone-100">
                <CardContent className="p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Total Cash Out</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(totalOut)}</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-stone-100">
                <CardContent className="p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Net Cashflow</p>
                  <p className={`text-xl font-bold ${netCashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(netCashflow)}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white border-stone-100">
                <CardContent className="p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Final Balance</p>
                  <p className={`text-xl font-bold ${finalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(finalBalance)}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      })()}

      {/* Cashflow Trend Chart */}
      <Card className="bg-white border-stone-100 shadow-sm">
        <CardHeader className="pb-3 border-b border-stone-100">
          <CardTitle className="text-base font-medium text-stone-800 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            Cashflow Trend
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={cashflowData.map(m => ({
                month: m.label,
                cashIn: Math.round(m.totalCashIn),
                cashOut: Math.round(-m.totalCashOut),
                net: Math.round(m.netCashflow),
                closing: Math.round(m.closingBalance),
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#78716c' }} interval={2} />
                <YAxis tick={{ fontSize: 11, fill: '#78716c' }} tickFormatter={v => `£${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e7e5e4', fontSize: '12px' }}
                  formatter={(value, name) => {
                    const labels = { cashIn: 'Cash In', cashOut: 'Cash Out', net: 'Net Cashflow', closing: 'Closing Balance' };
                    return [formatCurrency(Math.abs(value)), labels[name] || name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <ReferenceLine y={0} stroke="#a8a29e" strokeDasharray="3 3" />
                <Bar dataKey="cashIn" name="Cash In" fill="#22c55e" opacity={0.7} radius={[2, 2, 0, 0]} />
                <Bar dataKey="cashOut" name="Cash Out" fill="#ef4444" opacity={0.7} radius={[2, 2, 0, 0]} />
                <Line dataKey="net" name="Net Cashflow" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                <Line dataKey="closing" name="Closing Balance" stroke="#0ea5e9" strokeWidth={2.5} dot={false} strokeDasharray="6 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Cashflow Table */}
      <Card className="bg-white border-stone-100 overflow-hidden">
        <CardHeader className="border-b border-stone-100 pb-3">
          <CardTitle className="text-base font-medium text-stone-800">
            Monthly Cashflow Forecast ({FORECAST_MONTHS[0]?.fullLabel} – {FORECAST_MONTHS[FORECAST_MONTHS.length - 1]?.fullLabel})
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-stone-50 sticky top-0">
              <tr>
                <th className="text-left py-2 px-3 font-medium text-stone-600 border-b border-stone-100 sticky left-0 bg-stone-50 z-10">Category</th>
                {FORECAST_MONTHS.map(m => (
                  <th key={m.key} className="text-right py-2 px-2 font-medium text-stone-600 border-b border-stone-100 min-w-[70px]">
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Scenario Selection Row */}
              <tr className="bg-blue-50">
                <td className="py-2 px-3 font-medium text-blue-700 sticky left-0 bg-blue-50">Scenario (A–F)</td>
                {FORECAST_MONTHS.map(m => (
                  <td key={m.key} className="py-1 px-1 text-right">
                    <Select
                      value={scenarioPerMonth[m.key]}
                      onValueChange={val => setScenarioPerMonth(s => ({ ...s, [m.key]: val }))}
                    >
                      <SelectTrigger className="h-6 text-xs w-12 ml-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="E">E</SelectItem>
                        <SelectItem value="F">F</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                ))}
              </tr>

              {/* Revenue Section Header */}
              <tr className="bg-green-50">
                <td colSpan={FORECAST_MONTHS.length + 1} className="py-2 px-3 font-semibold text-green-800">REVENUE / CASH IN</td>
              </tr>

              {/* Confirmed Balance */}
              <tr className="hover:bg-stone-50">
                <td className="py-2 px-3 pl-6 text-stone-700 sticky left-0 bg-white">Confirmed Balance Payments</td>
                {cashflowData.map(m => (
                  <td key={m.key} className="py-2 px-2 text-right text-stone-700 font-medium">
                    {m.confirmedBalance > 0 ? formatCurrency(m.confirmedBalance) : '-'}
                  </td>
                ))}
              </tr>

              {/* New Sales Deposits */}
              <tr className="hover:bg-stone-50">
                <td className="py-2 px-3 pl-6 text-stone-700 sticky left-0 bg-white">New Sales Deposits (50%)</td>
                {cashflowData.map(m => (
                  <td key={m.key} className="py-2 px-2 text-right text-stone-700">
                    {formatCurrency(m.deposit)}
                  </td>
                ))}
              </tr>

              {/* Modelled Balance Income */}
              <tr className={`hover:bg-stone-50 ${!showModelledBalance ? 'opacity-40' : ''}`}>
                <td className="py-2 px-3 pl-6 text-stone-700 sticky left-0 bg-white">
                  <div className="flex items-center gap-2">
                    <span className={showModelledBalance ? 'text-purple-700' : 'text-stone-400 line-through'}>Modelled Balance Income (50%)</span>
                    <button
                      onClick={() => setShowModelledBalance(!showModelledBalance)}
                      className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                        showModelledBalance 
                          ? 'bg-purple-50 border-purple-200 text-purple-600 hover:bg-purple-100' 
                          : 'bg-stone-50 border-stone-200 text-stone-400 hover:bg-stone-100'
                      }`}
                      data-testid="toggle-modelled-balance"
                    >
                      {showModelledBalance ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </td>
                {cashflowData.map(m => (
                  <td key={m.key} className={`py-2 px-2 text-right ${showModelledBalance ? 'text-purple-700' : 'text-stone-300'}`}>
                    {showModelledBalance 
                      ? (m.modelledBalance > 0 ? formatCurrency(m.modelledBalance) : '-')
                      : '-'
                    }
                  </td>
                ))}
              </tr>

              {/* Additional Revenue */}
              <tr className="bg-blue-50">
                <td className="py-2 px-3 pl-6 text-blue-700 sticky left-0 bg-blue-50">Additional Revenue</td>
                {FORECAST_MONTHS.map(m => (
                  <td key={m.key} className="py-1 px-1 text-right">
                    <Input
                      type="number"
                      value={additionalRevenue[m.key] || ''}
                      onChange={e => setAdditionalRevenue(r => ({ ...r, [m.key]: Number(e.target.value) || 0 }))}
                      className="h-6 text-xs w-16 ml-auto text-right"
                      placeholder="0"
                    />
                  </td>
                ))}
              </tr>

              {/* Total Cash In */}
              <tr className="bg-green-100 font-semibold">
                <td className="py-2 px-3 text-green-800 sticky left-0 bg-green-100">TOTAL CASH IN</td>
                {cashflowData.map(m => (
                  <td key={m.key} className="py-2 px-2 text-right text-green-800">
                    {formatCurrency(m.totalCashIn)}
                  </td>
                ))}
              </tr>

              {/* Costs Section Header */}
              <tr className="bg-red-50">
                <td colSpan={FORECAST_MONTHS.length + 1} className="py-2 px-3 font-semibold text-red-800">
                  <div className="flex items-center justify-between">
                    <span>COSTS / CASH OUT {teamScaling && <span className="text-blue-600 text-xs font-normal ml-2">(Team Scaling active — {BASELINE_WEDDINGS}/mo baseline)</span>}</span>
                    <button
                      onClick={() => setCostsUnlocked(!costsUnlocked)}
                      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md transition-colors border"
                      style={{
                        background: costsUnlocked ? '#fef3c7' : '#f5f5f4',
                        borderColor: costsUnlocked ? '#f59e0b' : '#d6d3d1',
                        color: costsUnlocked ? '#92400e' : '#78716c'
                      }}
                      data-testid="costs-lock-toggle"
                    >
                      {costsUnlocked ? (
                        <><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg> Editing</>
                      ) : (
                        <><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> Locked</>
                      )}
                    </button>
                  </div>
                </td>
              </tr>

              {/* Team Scaling indicator row */}
              {teamScaling && (
                <tr className="bg-blue-50/60">
                  <td className="py-1 px-3 pl-4 text-[11px] font-semibold text-blue-600 sticky left-0 bg-blue-50/60">
                    TEAM SCALE FACTOR (baseline: {BASELINE_WEDDINGS} weddings/mo)
                  </td>
                  {cashflowData.map(m => (
                    <td key={m.key} className="py-1 px-2 text-right text-[11px] font-semibold text-blue-600">
                      {m.scaleFactor !== 1 ? `${m.scaleFactor.toFixed(1)}x` : '1x'}
                    </td>
                  ))}
                </tr>
              )}

              {/* VAT Row - first cost line */}
              <tr className="bg-amber-50/40 hover:bg-amber-50">
                <td className="py-1.5 px-3 pl-4 text-stone-700 font-medium sticky left-0 bg-amber-50/40 text-sm">
                  VAT (20% of income)
                </td>
                {cashflowData.map(m => (
                  <td key={m.key} className="py-1.5 px-2 text-right text-amber-700 font-medium text-sm">
                    {m.vat > 0 ? formatCurrency(m.vat) : '-'}
                  </td>
                ))}
              </tr>

              {/* Individual cost line items by section */}
              {COST_LINE_ITEMS.map(section => (
                <React.Fragment key={section.section}>
                  <tr className="bg-stone-50/60">
                    <td colSpan={FORECAST_MONTHS.length + 1} className="py-1 px-3 pl-4 text-[11px] font-semibold text-stone-500 uppercase tracking-wider sticky left-0 bg-stone-50/60">
                      {section.section}
                    </td>
                  </tr>
                  {section.items.map(item => (
                    <tr key={item.key} className="hover:bg-stone-50">
                      <td className="py-1 px-3 pl-6 text-stone-600 sticky left-0 bg-white text-sm">{item.label}</td>
                      {cashflowData.map(m => (
                        <td key={m.key} className="py-1 px-2 text-right text-stone-600 text-sm">
                          {costsUnlocked ? (
                            <input
                              type="number"
                              value={assumptions[item.key] ?? m.costs?.[item.costKey] ?? 0}
                              onChange={(e) => setAssumptions(a => ({ ...a, [item.key]: Number(e.target.value) || 0 }))}
                              className="w-20 text-right text-sm border border-amber-300 bg-amber-50 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
                              data-testid={`cost-input-${item.key}`}
                            />
                          ) : (
                            formatCurrency(m.costs?.[item.costKey] ?? 0)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}

              {/* Total Cash Out */}
              <tr className="bg-red-100 font-semibold">
                <td className="py-2 px-3 text-red-800 sticky left-0 bg-red-100">TOTAL CASH OUT</td>
                {cashflowData.map(m => (
                  <td key={m.key} className="py-2 px-2 text-right text-red-800">
                    {formatCurrency(m.totalCashOut)}
                  </td>
                ))}
              </tr>

              {/* Net Cashflow Section */}
              <tr className="bg-stone-100">
                <td colSpan={FORECAST_MONTHS.length + 1} className="py-2 px-3 font-semibold text-stone-800">NET CASHFLOW & POSITION</td>
              </tr>

              {/* Net Monthly */}
              <tr className="hover:bg-stone-50">
                <td className="py-2 px-3 pl-6 text-stone-700 font-medium sticky left-0 bg-white">Net Monthly Cashflow</td>
                {cashflowData.map(m => (
                  <td key={m.key} className={`py-2 px-2 text-right font-medium ${m.netCashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(m.netCashflow)}
                  </td>
                ))}
              </tr>

              {/* Opening Balance */}
              <tr className="hover:bg-stone-50">
                <td className="py-2 px-3 pl-6 text-stone-600 sticky left-0 bg-white">Opening Balance</td>
                {cashflowData.map(m => (
                  <td key={m.key} className="py-2 px-2 text-right text-stone-600">
                    {formatCurrency(m.openingBalance)}
                  </td>
                ))}
              </tr>

              {/* Closing Balance */}
              <tr className="bg-stone-200 font-bold">
                <td className="py-2 px-3 text-stone-800 sticky left-0 bg-stone-200">Closing Balance</td>
                {cashflowData.map(m => (
                  <td key={m.key} className={`py-2 px-2 text-right ${m.closingBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(m.closingBalance)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Warning for negative months */}
      {negativeMonths.length > 0 && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Cash Flow Warning</p>
                <p className="text-xs text-red-700 mt-1">
                  Some months show negative closing balances. Consider adjusting scenarios, reducing costs, or adding additional revenue.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {negativeMonths.map(m => (
                    <span key={m.key} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                      {m.fullLabel}: {formatCurrency(m.closingBalance)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
